import { Component, Input, OnInit, OnDestroy } from "@angular/core";
import { FormBuilder, Validators, ValidatorFn, AbstractControl, ValidationErrors, AsyncValidatorFn } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ProDefiniciones } from "@shared/interfaces/request/proDefiniciones";
import { ConceptosService } from "@p-p-portafolio/services/conceptos.service";
import { ProDefinicionesService } from "@p-p-portafolio/services/pro-definiciones.service";
import { TasasService } from "@p-p-portafolio/services/tasas.service";
import { UserService } from "@pages/user/services/user.service";
import { InjectableService } from "@shared/utils/injectable.service";
import { SwalService } from "@shared/utils/swal.service";
import { Observable, of, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { Location } from "@angular/common";
import { SharedDataService } from "@shared/utils/shared-data.service";

export interface IDefMoratorio {
    promocion: string;
    numVar: number;
    descripcion: string;
    claveTasa: string;
    valorFijo: number;
    amortInf: number;
    amortSup: number;
    formula: string;
    porcentaje: number;
    concepto: number;
    conceptoAdicional: number;
    user: null;
}

@Component({
    selector: "app-def-moratorios",
    templateUrl: "./def-moratorios.component.html",
    styleUrls: ["./def-moratorios.component.css"],
})
export class DefMoratoriosComponent implements OnInit, OnDestroy {
    @Input() modelRecived: ProDefiniciones;
    private _user: string = this._userServices.userCurrent;
    public promocion: string = '';
    private subscriptions: Subscription = new Subscription();
    public breakpoint: number;
    public tasas: any[];
    public conceptos: any[];
    public calculate: any[];
    public aplicateTo: any[];
    public calculate2: any[];
    public formPrincipal: boolean = true;
    public selectTasa: boolean;
    public selectTasa2: boolean;
    public selectCocepto: boolean;
    public selectCocepto2: boolean;
    public loading: boolean;
    modelo: ProDefiniciones;
    
    constructor(
        private _fb: FormBuilder,
        private _swalService: SwalService,
        private _proDefServices: ProDefinicionesService,
        private _userServices: UserService,
        private _injectServices: InjectableService,
        private _tasasServices: TasasService,
        private _conceptosServices: ConceptosService,
        private sharedDataService: SharedDataService,
        private location: Location,
        private route: ActivatedRoute
    ) {
    }

    ngOnInit(): void {
        // Obtener promoción de la ruta
        const routeParams = this.route.snapshot.paramMap;
        const promocionFromRoute = routeParams.get('promocion');
        
        // Intentar obtener la promoción de diferentes fuentes en orden de prioridad
        if (promocionFromRoute) {
            this.promocion = promocionFromRoute;
        } else {
            // Intentar desde sessionStorage
            const savedPromocion = sessionStorage.getItem('selectedPromocion');
            if (savedPromocion) {
                try {
                    const promocionData = JSON.parse(savedPromocion);
                    this.promocion = promocionData.sPromocion || promocionData.promocion || '';
                } catch (error) {
                    console.error('Error al parsear promoción desde sessionStorage:', error);
                }
            }
            
            // Si aún no hay promoción, intentar desde localStorage (fallback)
            if (!this.promocion) {
                this.promocion = localStorage.getItem("prom") || '';
            }
        }

        // Actualizar el formulario con la promoción
        if (this.promocion) {
            this.form.patchValue({ promocion: this.promocion });
        }

        // Suscribirse al servicio compartido
        const sharedDataSub = this.sharedDataService.currentData$.subscribe((data: IDefMoratorio) => {
            if (data && data.promocion) {
                // Actualizar promoción si viene del servicio
                if (!this.promocion) {
                    this.promocion = data.promocion;
                    this.form.patchValue({ promocion: this.promocion });
                }
                this.procesarDatosDefinicion(data);
            }
        });
        this.subscriptions.add(sharedDataSub);

        // Cargar datos iniciales
        this.readCalculate();
        this.readAplicateTo();
        this.readCalculate2();
        this.readConceptos();
        this.injectConcepto();
        this.readTasas();
        this.injectTasa();
        this.onBreakPonit();
    }

    ngOnDestroy(): void {
        // Limpiar todas las suscripciones
        this.subscriptions.unsubscribe();
    }

    private procesarDatosDefinicion(data: IDefMoratorio): void {
        // Solo procesar si data no es null y tiene las propiedades necesarias
        if (data && data.promocion) {
            // Verificar si realmente existe una definición (tiene formula y numVar válido)
            const tieneDefinicionValida = data.formula && data.formula.trim() !== '' && data.numVar && data.numVar > 0;
            
            // Actualizar el formulario con el numVar si existe
            if (data.numVar && data.numVar > 0) {
                this.form.patchValue({ numVar: data.numVar });
            }
            
            // Solo establecer modelRecived si hay una definición válida (modo edición)
            if (tieneDefinicionValida) {
                this.modelRecived = {
                    amortInf: data.amortInf || 0,
                    amortSup: data.amortSup || 0,
                    claveTasa: data.claveTasa || '',
                    concepto: data.concepto || 0,
                    conceptoAdicional: data.conceptoAdicional || 0,
                    descripcion: data.descripcion || '',
                    formula: data.formula || '',
                    numVar: data.numVar || 2,
                    porcentaje: data.porcentaje || 0,
                    promocion: data.promocion,
                    user: data.user || null,
                    valorFijo: data.valorFijo || 0,
                };
                this.getFormula(data);
            } else {
                // Modo creación: limpiar modelRecived y solo usar numVar si existe
                this.modelRecived = null;
                if (data.claveTasa) {
                    this.form.patchValue({ claveTasa: data.claveTasa });
                }
            }
        }
    }

    form = this._fb.group({
        promocion: [this.promocion],
        numVar: [
            2,
            {
                validators: [],
                asyncValidators: [this.singleDefinicion()],
                updateOn: "blur",
            },
        ],
        descripcion: [""],
        claveTasa: [""],
        valorFijo: ["0.0"],
        amortInf: [0],
        amortSup: [0],
        formula: [""],
        porcentaje: ["0.0"],
        concepto: [""],
        conceptoAdicional: [0],
        user: this._user,
        // Valores que van en formula
        calculate: [1],
        calculate2: [1],
        aplicateTo: [1],
        claveTasa2: [null],
        diasGracia: [null],
        calculados: [null],
        aplicacionTasa: [null],
        aplicaOption: [null],
        ivaMoratorios: [0],
        ivaMoratoriosOption: [null],
        fijo: [0],
        minimo: [null],
    });

    readCalculate() {
        let iTipo: number = 20;
        this._proDefServices.proDefGasValores(iTipo).subscribe((res) => {
            this.calculate = res.data;
        });
    }

    readAplicateTo() {
        let iTipo: number = 21;
        this._proDefServices.proDefGasValores(iTipo).subscribe((res) => {
            this.aplicateTo = res.data;
        });
    }

    readCalculate2() {
        let iTipo: number = 22;
        this._proDefServices.proDefGasValores(iTipo).subscribe((res) => {
            this.calculate2 = res.data;
        });
    }

    readConceptos() {
        this._conceptosServices.getAll().subscribe((res) => {
            this.conceptos = res;
        });
    }

    injectConcepto() {
        const conceptoSub = this._injectServices.disparadorConcepto.subscribe((res) => {
            if (this.selectCocepto2) this.form.patchValue({ conceptoAdicional: res.model.data });
            else this.form.patchValue({ concepto: res.model.data });
            if (res.model.success) this.changeBoolean("form");
        });
        this.subscriptions.add(conceptoSub);
    }

    readTasas() {
        this._tasasServices.read().subscribe((res) => {
            this.tasas = res.data;
        });
    }

    getModel() {
        // Usar numVar del formulario o del modelRecived si está en modo edición, o 2 por defecto
        const numVar = this.modelRecived?.numVar || this.form?.controls.numVar?.value || 2;
        this.modelo = {
            promocion: this.promocion,
            numVar: numVar,
            descripcion: "DEFINICION DE CALCULO DE MORATORIOS",
            formula: this.setFormula(),
            claveTasa: this.form?.controls.claveTasa.value,
            valorFijo: parseInt(this.form?.controls.valorFijo.value),
            amortInf: 0,
            amortSup: 0,
            porcentaje: 0,
            concepto: 0,
            conceptoAdicional: 0,
            user: this._user,
        };
        return this.modelo;
    }

    injectTasa() {
        const tasaSub = this._injectServices.disparadorTasas.subscribe((res) => {
            if (this.selectTasa2) this.form.patchValue({ claveTasa2: res.model.data });
            else this.form.patchValue({ claveTasa: res.model.data });
            if (res.model.success) this.changeBoolean("form");
        });
        this.subscriptions.add(tasaSub);
    }

    changeBoolean(change: string) {
        switch (change) {
            case "form":
                this.selectCocepto = false;
                this.selectCocepto2 = false;
                this.selectTasa = false;
                this.selectTasa2 = false;
                this.formPrincipal = true;
                break;
            case "conceptos":
                this.formPrincipal = false;
                this.selectCocepto = true;
                break;
            case "tasas":
                this.formPrincipal = false;
                this.selectTasa = true;
                break;
            case "conceptos2":
                this.formPrincipal = false;
                this.selectCocepto = true;
                this.selectCocepto2 = true;
                break;
            case "tasas2":
                this.formPrincipal = false;
                this.selectTasa2 = true;
                this.selectTasa = true;
                break;
        }
    }

    create() {
        if (this.form.valid) {
            this.loading = true;
            //model = { ...model, formula: this.setFormula() };
            this._proDefServices.create(this.getModel()).subscribe((res) => {
                this.loading = false;
                if (res.exito === 1) {
                    //   this._dialogRef.close();
                    this._swalService.success("Agregado");
                } else this._swalService.error("No Agregado");
            });
        }
    }

    update() {
        if (this.form.valid) {
            //Valores que no van en Fórmula
            this.modelRecived.claveTasa = this.form.controls["claveTasa"].value;
            this.modelRecived.porcentaje = this.form.controls["ivaMoratorios"].value;
            this.modelRecived.valorFijo = this.form.controls["fijo"].value;
            this.loading = true;
            let model = { ...this.modelRecived, formula: this.setFormula() };
            this._proDefServices.update(model).subscribe((res) => {
                this.loading = false;
                if (res.exito === 1) {
                    //   this._dialogRef.close();
                    this._swalService.success("Editado");
                } else this._swalService.error("No Editado");
            });
        }
    }

    formValue(value: string) {
        return this.form.get(value);
    }

    variableValidation(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            const variable = control.value;

            if (!variable) return;

            if (variable < 501 || variable > 600) return { variableValidation: true };

            return null;
        };
    }

    singleDefinicion(): AsyncValidatorFn {
        return (control: AbstractControl): Promise<ValidationErrors> | Observable<any> => {
            if (this.modelRecived) return of(null);
            
            // Validar que tenemos promoción antes de hacer la petición
            if (!this.promocion || this.promocion === 'null' || this.promocion === 'undefined') {
                console.warn('singleDefinicion: No hay promoción disponible para validar');
                return of(null); // Retornar válido si no hay promoción (evitar error)
            }
            
            return this._proDefServices.readOne(this.promocion, control.value).pipe(
                map((res) => {
                    if (res && res.exito === 1) return { singleDefinicion: true };
                    return null;
                })
            );
        };
    }

    setFormula(): string {
        let sep = "|";

        let lst =
            this.form.controls["aplicacionTasa"].value +
            sep +
            this.form.controls["calculados"].value +
            sep +
            this.form.controls["aplicaOption"].value +
            sep +
            this.form.controls["diasGracia"].value +
            sep +
            this.form.controls["ivaMoratoriosOption"].value +
            sep +
            this.form.controls["minimo"].value +
            sep +
            this.form.controls["claveTasa2"].value;

        lst = lst.replace(/true/g, "1").replace(/false/g, "0");

        return lst;
    }

    getFormula(data: IDefMoratorio): void {
        // Validar que la fórmula no esté vacía
        if (!data.formula || data.formula.trim() === '') {
            return;
        }
        
        let formulaSep: string[] = data.formula.split("|");

        //Valores que no van en Fórmula
        this.form.controls["claveTasa"].patchValue(data.claveTasa);
        this.form.controls["ivaMoratorios"].patchValue(data.porcentaje);
        this.form.controls["fijo"].patchValue(data.valorFijo);

        // Valores que van en formulas
        this.form.controls["aplicacionTasa"].patchValue(parseInt(formulaSep[0]));
        this.form.controls["calculados"].patchValue(parseInt(formulaSep[1]));
        this.form.controls["aplicaOption"].patchValue(parseInt(formulaSep[2]));
        this.form.controls["diasGracia"].patchValue(parseInt(formulaSep[3]));
        this.form.controls["ivaMoratoriosOption"].patchValue(parseInt(formulaSep[4]));
        this.form.controls["minimo"].patchValue(parseInt(formulaSep[5]));
        this.form.controls["claveTasa2"].patchValue(formulaSep[6]);
    }

    onBreakPonit() {
        this.breakpoint = window.innerWidth <= 200 ? 1 : 2;
    }

    onResize(event) {
        this.breakpoint = event.target.innerWidth <= 200 ? 1 : 2;
    }

    close() {
        // this._dialogRef.close();
        this.location.back();
    }
}
